// «Ждёт тебя»: вопросы агентов (эскалации) с формой ответа и «Готово» — ваши задачи, которые агенты закончили.
import { useMemo, useState } from 'react'
import { api } from '../api.ts'
import { DoneCard } from '../components/DoneCard.tsx'
import { EscalationCard } from '../components/EscalationCard.tsx'
import { Button, Empty, ErrorBox, Loading, PageHeader, Segmented, Tabs } from '../components/ui.tsx'
import { useAsync, useDebounced } from '../lib/hooks.ts'
import { isOwnerTask } from '../lib/outcome.ts'
import { setSearchParams, useSearchParam } from '../router.tsx'
import { refreshStats, useLive, useReconnect, useStream } from '../store.ts'
import { toastError } from '../toast.ts'
import { ESCALATION_TYPE_LABELS, type Escalation, type EscalationType, type TaskCard } from '../types.ts'

type Filter = 'all' | EscalationType
type InboxTab = 'questions' | 'done'

export function InboxPage() {
  const param = useSearchParam('tab')
  const openCount = useLive((s) => s.stats?.open_escalations ?? 0)
  const doneCount = useLive((s) => s.stats?.done_unseen ?? 0)
  // без явного выбора: есть вопросы — сначала они, иначе — готовые задачи
  const tab: InboxTab = param === 'done' || param === 'questions' ? param : openCount === 0 && doneCount > 0 ? 'done' : 'questions'
  return (
    <div className="page max-w-[980px]">
      <PageHeader
        title="Ждёт тебя"
        lede={
          tab === 'questions'
            ? 'Всё, что агентам нужно от тебя: решения, уточнения, одобрения, сообщения от твоего имени на отправку и блокеры.'
            : 'Ваши задачи, которые агенты закончили: коротко, что получилось. Подробности — в самой задаче.'
        }
      />
      <Tabs<InboxTab>
        label="Разделы"
        className="mb-5"
        value={tab}
        onChange={(v) => setSearchParams({ tab: v })}
        items={[
          { id: 'questions', label: 'Вопросы', count: openCount },
          { id: 'done', label: 'Готово', count: doneCount },
        ]}
      />
      {tab === 'questions' ? <QuestionsTab /> : <DoneTab />}
    </div>
  )
}

function DoneTab() {
  const done = useAsync(
    (signal) =>
      api.get<{ unseen: TaskCard[]; recent: TaskCard[] }>('/api/inbox/done', {
        signal,
      }),
    [],
  )
  const reloadSoon = useDebounced(done.reload, 800)
  const [busy, setBusy] = useState(false)
  useStream((ev) => {
    if (ev.type === 'task' && isOwnerTask(ev.data)) reloadSoon()
  })
  useReconnect(done.reload)

  const seen = async (ids: number[]) => {
    const moved = (done.data?.unseen ?? []).filter((c) => ids.includes(c.id))
    done.setData((cur) =>
      cur
        ? {
            unseen: cur.unseen.filter((c) => !ids.includes(c.id)),
            recent: [...moved, ...cur.recent],
          }
        : cur,
    )
    try {
      if (ids.length === 1) await api.post(`/api/tasks/${ids[0]}/seen`, {})
      else await api.post('/api/inbox/done/seen-all', {})
    } catch (e) {
      toastError(e, 'Не удалось отметить')
      done.reload()
    }
    void refreshStats()
  }

  if (done.error && !done.data) return <ErrorBox error={done.error} onRetry={done.reload} />
  if (!done.data) return <Loading />
  const { unseen, recent } = done.data
  return (
    <>
      {unseen.length === 0 ? (
        <Empty icon="check">
          <div className="font-medium text-fg">Новых готовых задач нет</div>
          <div className="mt-0.5 text-[12.5px]">
            Когда агенты закончат вашу задачу, она появится здесь, в меню загорится зелёный счётчик и придёт уведомление.
          </div>
        </Empty>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="ct">Новые · {unseen.length}</h2>
            <span className="ml-auto" />
            {unseen.length > 1 ? (
              <Button
                size="sm"
                variant="ghost"
                icon="check"
                loading={busy}
                onClick={() => {
                  setBusy(true)
                  void seen(unseen.map((c) => c.id)).finally(() => setBusy(false))
                }}
              >
                Все просмотрены
              </Button>
            ) : null}
          </div>
          <div className="flex flex-col gap-3">
            {unseen.map((c) => (
              <DoneCard key={c.id} card={c} onSeen={() => void seen([c.id])} />
            ))}
          </div>
        </>
      )}
      <section className="mt-10">
        <h2 className="ct mb-2.5">Просмотренные за неделю</h2>
        {recent.length === 0 ? (
          <Empty>Пока ничего</Empty>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((c) => (
              <DoneCard key={c.id} card={c} compact />
            ))}
          </div>
        )}
      </section>
    </>
  )
}

function QuestionsTab() {
  const open = useAsync((signal) => api.get<Escalation[]>('/api/escalations?status=open', { signal }), [])
  const answered = useAsync((signal) => api.get<Escalation[]>('/api/escalations?status=answered', { signal }), [])
  const [filter, setFilter] = useState<Filter>('all')

  useStream((ev) => {
    if (ev.type !== 'escalation') return
    const e = ev.data
    open.setData((cur) => {
      if (!cur) return cur
      const without = cur.filter((x) => x.id !== e.id)
      if (e.status !== 'open') return without
      const prev = cur.find((x) => x.id === e.id)
      return prev ? cur.map((x) => (x.id === e.id ? { ...x, ...e } : x)) : [e, ...cur]
    })
    if (e.status === 'answered') {
      answered.setData((cur) => (cur ? [e, ...cur.filter((x) => x.id !== e.id)] : cur))
    }
  })
  useReconnect(() => {
    open.reload()
    answered.reload()
  })

  const onAnswered = (e: Escalation) => {
    open.setData((cur) => cur?.filter((x) => x.id !== e.id))
    answered.setData((cur) => (cur ? [e, ...cur.filter((x) => x.id !== e.id)] : cur))
    void refreshStats()
  }

  const list = useMemo(() => {
    const items = [...(open.data ?? [])]
    // блокеры, ваши действия и одобрения — выше, затем старые вопросы раньше новых
    const weight: Record<EscalationType, number> = { blocker: 0, action: 1, message: 2, approval: 3, business_decision: 4, clarification: 5 }
    items.sort((a, b) => weight[a.type] - weight[b.type] || a.created_at.localeCompare(b.created_at))
    return filter === 'all' ? items : items.filter((e) => e.type === filter)
  }, [open.data, filter])

  const counts = useMemo(() => {
    const c: Partial<Record<EscalationType, number>> = {}
    for (const e of open.data ?? []) c[e.type] = (c[e.type] ?? 0) + 1
    return c
  }, [open.data])

  const recent = (answered.data ?? []).slice(0, 15)

  return (
    <>
      {open.data && open.data.length ? (
        // типов много и подписи длинные: переключатель переносится строками, а не уезжает за край экрана
        <div className="mb-4 [&_[role=radio]]:whitespace-nowrap [&>[role=radiogroup]]:max-w-full [&>[role=radiogroup]]:flex-wrap">
          <Segmented<Filter>
            label="Тип вопроса"
            value={filter}
            onChange={setFilter}
            items={[
              { id: 'all', label: `Все · ${open.data.length}` },
              ...(Object.keys(ESCALATION_TYPE_LABELS) as EscalationType[])
                .filter((t) => counts[t])
                .map((t) => ({
                  id: t,
                  label: `${ESCALATION_TYPE_LABELS[t]} · ${counts[t]}`,
                })),
            ]}
          />
        </div>
      ) : null}

      {open.error && !open.data ? (
        <ErrorBox error={open.error} onRetry={open.reload} />
      ) : !open.data ? (
        <Loading />
      ) : list.length === 0 ? (
        <Empty icon="check">
          <div className="font-medium text-fg">Сейчас агентам от тебя ничего не нужно</div>
          <div className="mt-0.5 text-[12.5px]">Новые вопросы появятся здесь сразу, а в меню загорится счётчик.</div>
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          {list.map((e) => (
            <EscalationCard key={e.id} esc={e} onAnswered={onAnswered} />
          ))}
        </div>
      )}

      <section className="mt-10">
        <h2 className="ct mb-2.5">Недавно отвеченные</h2>
        {answered.error && !answered.data ? (
          <ErrorBox error={answered.error} onRetry={answered.reload} />
        ) : !answered.data ? (
          <Loading />
        ) : recent.length === 0 ? (
          <Empty>Пока ничего</Empty>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((e) => (
              <EscalationCard key={e.id} esc={e} compact />
            ))}
          </div>
        )}
      </section>
    </>
  )
}
